import fetch from 'isomorphic-fetch';
import qs from 'query-string';
import {
	setAccessToken,
	setAccessTokenExpiryTime,
} from './actions/authActions';
import { LOCAL_STORAGE_KEYS } from './constants/constants';
// eslint-disable-next-line import/no-cycle
import { store, userLogout } from './store';
import { getHomePageUrl, BASE_PATH_SUFFIX } from './utils';
import { gaEventApiCall } from './utils/googleAnalytics';
import Backend from './enums/api';

const methods = {
	GET: 'GET',
	POST: 'POST',
	PUT: 'PUT',
	DELETE: 'DELETE',
	PATCH: 'PATCH',
};

const responseTypes = {
	TEXT: 'text',
	JSON: 'json',
	BINARY: 'binary',
	DOWNLOAD: 'download',
};

const endpoints = {
	confirmBookingChange: {
		path: 'seat-reservation-amendments/booking-change',
		method: methods.POST,
	},
	validateShopping: {
		path: 'seat-reservation-amendments/validate',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	confirmRefundOffer: {
		path: 'refund-offers',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	getPaymentContextType: {
		path: 'context-type/realm-and-requester',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getExternalPaymentPciUrl: {
		path: 'external-payment-pci/url',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	getExternalPaymentPciOperationStatus: {
		path: 'external-payment-pci/operations/:operationId',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getJourneysList: {
		path: 'seat-reservation-amendments/shopping-search',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getAccountUsers: {
		path: 'getAccountUsersJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getSmartCardPrintingLocations: {
		path: 'smartcards/printing-locations',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	printSmartcard: {
		path: 'smartcards',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	getUserProfile: {
		path: 'getUserProfileAMJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	addUserProfile: {
		path: 'addUserProfileAMJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	updateUserProfile: {
		path: 'modifyUserProfileAMJSONP',
		method: methods.POST,
		noRedirect: true,
		responseType: responseTypes.JSON,
	},
	getUserSettings: {
		path: 'widgets/data/getDataAndPropertiesJSON',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	userSearch: {
		path: 'userSearchJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getRoles: {
		path: 'roles',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	deleteUser: {
		path: 'deleteUserAMJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	accountSearch: {
		path: 'accountSearchJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getAccountGroupData: {
		path: 'accountGroupDataJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getAccountGroupLabels: {
		path: 'accountGroupLabelsJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getAccountLabels: {
		path: 'accountLabelsJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	submitAccountGroupData: {
		path: 'accountGroupCreateEditJSONP',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	deleteAccountGroup: {
		path: 'deleteAccountGroupAMJSONP',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	getAccountData: {
		path: 'accountDataJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	submitAccountData: {
		path: 'accountCreateEditJSONP',
		method: methods.POST,
		noRedirect: true,
		responseType: responseTypes.JSON,
	},
	deleteAccount: {
		path: 'deleteAccountAMJSONP',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	logOut: {
		path: 'logoutJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	changeUserPassword: {
		path: 'changePasswordAMJSONP',
		method: methods.POST,
		noRedirect: true,
		responseType: responseTypes.JSON,
	},
	stationSearch: {
		path: 'stationSearchJSON',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	refreshUserState: {
		path: 'refreshUserStateJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	shoppingSearch: {
		path: 'shoppingSearchJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	bookingValidate: {
		path: 'bookingValidateJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	tdoSmsCallingCodes: {
		path: 'bookingGetTDOSMSCallingCodesJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	bookingCreate: {
		path: 'bookingCreateJSONP',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	bookingSetTdo: {
		path: 'bookingSetTDOOptionsJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	updateCustomInfo: {
		path: 'bookingUpdateCustomInfoJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	bookingSearch: {
		path: 'bookingsSearchJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	loadBookingDetails: {
		path: 'bookingSearchDetailsJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getBookingAmendmentsInfo: {
		path: 'bookings/amendments',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	addBookingNote: {
		path: 'bookingAddNoteJSONP',
		method: methods.POST,
		noRedirect: true,
		responseType: responseTypes.JSON,
	},
	addSupplierNote: {
		path: 'bookingAddSupplierNoteJSONP',
		method: methods.POST,
		noRedirect: true,
		responseType: responseTypes.JSON,
	},
	addCustomInfo: {
		path: 'bookingAddCustomInformationJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getPaymentDue: {
		path: 'bookingGetPaymentsDueJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	submitPayment: {
		path: 'bookingAddPaymentJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getOrderDetails: {
		path: 'bookingOrderDetailsJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getCancelOrderDetails: {
		path: 'bookingCancellationTicketableFareGroup',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	applyFeeDiscount: {
		path: 'updateBookingApplyFeeDiscountJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	submitCancelBooking: {
		path: 'cancelBooking',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	submitOrderCancel: {
		path: 'bookingSearchCancelOrdersJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getCancellationPenalty: {
		path: 'bookingCancellationPenalty',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getRefundDetails: {
		path: 'bookingSearchRefundablePaymentsJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	submitRefund: {
		path: 'updateBookingIssueRefundJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	bookingAddOrder: {
		path: 'bookingAddOrderJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getConfirmDetails: {
		path: 'bookingSearchConfirmDataJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	confirmOrder: {
		path: 'bookingConfirmOrderJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	addFeeDiscount: {
		path: 'updateBookingApplyFeeDiscountJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	amendCTR: {
		path: 'bookingAmmendCTRJSONP',
		method: methods.GET,
		responseType: responseTypes.TEXT,
	},
	loadEmailConfirmation: {
		path: 'bookingGetEmailConfirmationDataJSON',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	sendEmailConfirmation: {
		path: 'bookingEmailConfirmationSend',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getTrackingInfo: {
		path: 'bookingGetTrackingInfoJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	setTrackingInfo: {
		path: 'bookingSetTrackingInfoJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getCoupons: {
		path: 'bookingSearchCouponDetailsJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	setCoupons: {
		path: 'bookingReceiveCouponsJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getStationTOD: {
		path: 'stationTODRequestJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getSmartCardTdoStations: {
		path: 'smartCardStationsRequestJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getStationData: {
		path: 'stationDataRequestJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getAccessToken: {
		path: 'authenticate',
		method: methods.POST,
		responseType: responseTypes.JSON,
		noRedirect: true,
	},
	getRefreshToken: {
		path: 'authenticate/refresh',
		method: methods.POST,
		responseType: responseTypes.JSON,
		noRedirect: true,
	},
	resetUserPassword: {
		path: 'resetPasswordAMJSONP',
		responseType: responseTypes.JSON,
		method: methods.GET,
		noRedirect: true,
	},
	resetUserPasswordTokenCheck: {
		path: 'resetPasswordTokenCheckJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
		noRedirect: true,
	},
	forgotPassword: {
		path: 'forgotPasswordAMJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	isAuth: {
		path: 'isAuthJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getAuthRefunds: {
		path: 'bookingSearchIssuedCouponDetailsJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	setAuthRefunds: {
		path: 'bookingAuthorizeRefundJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	acceptTandC: {
		path: 'acceptTandCJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	sendTicket: {
		path: 'bookingSendClaimValueDocumentUrlJSONP',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	resendClaimTicket: {
		path: 'bookingResendClaimVDUrlJSONP',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	getExchangeOrderDetails: {
		path: 'bookingOrderExchangeDataJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	updateCardCollection: {
		path: 'updateCardCollection',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	suspendSecureTicket: {
		path: 'bookings/secure-tickets/suspension',
		method: methods.POST,
	},
	activateSecureTicket: {
		path: 'bookings/secure-tickets/activation',
		method: methods.POST,
	},
	resetSecureTicketDeviceId: {
		path: 'bookings/secure-tickets/device-id-reset',
		method: methods.POST,
	},
	resetMobileTicketDeviceId: {
		path: 'bookings/mobile-tickets/device-id-reset',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	exchangeSearch: {
		path: 'bookingExchangeSearchJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	exchangeSummary: {
		path: 'exchangeSummaryJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	processExchange: {
		path: 'processExchangeJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getAnalytics: {
		path: 'widgets/data/getAnalyticsJSON',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getPaymentData: {
		path: 'paymentDataJSON',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	deletePersonalInformation: {
		path: 'bookingDeletePersonalInformationJSON',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	submitPassengerInformation: {
		path: 'updateBookingPassengerInformationJSON',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	updatePassengerCustomInformation: {
		path: 'updatePassengerCustomInformationJSON',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	getIntermediateTravelPoints: {
		path: 'intermediateTravelpointsJSONP',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getExportedUsers: {
		path: 'exportUsersJSONP',
		method: methods.GET,
		responseType: responseTypes.BINARY,
	},
	getEligibleOuigoVouchers: {
		path: 'v1/ouigo/vouchers/eligible',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getFinalizedOuigoVouchers: {
		path: 'v1/ouigo/vouchers/finalized',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	finalizeOuigoVouchers: {
		path: 'v1/ouigo/vouchers/finalize',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	exportFinalizedOuigoVouchers: {
		path: 'v1/ouigo/vouchers/finalized/export',
		method: methods.GET,
		responseType: responseTypes.DOWNLOAD,
	},
	getFeatureToggles: {
		path: 'whitelabel/allFeatureToggles',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	toggleFeature: {
		path: 'whitelabel/toggleFeature',
		method: methods.POST,
		responseType: responseTypes.JSON,
	},
	getSeatMap: {
		path: 'whitelabel/seatmap/transactional/v2',
		method: methods.GET,
		responseType: responseTypes.JSON,
	},
	getCustomerAccountData: {
		domain: process.env.CUSTOMER_ACCOUNTS_API_HOST,
		path: 'v1/accounts',
		method: methods.GET,
		responseType: responseTypes.JSON,
		urlOptions: { encode: false },
	},
	customerAccountActions: {
		path: 'customer-accounts',
		method: methods.POST,
		responseType: null,
	},
	getCampaigns: {
		path: 'campaigns/list',
		method: methods.GET,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
	},
	getPromotionCampaignReports: {
		path: 'campaigns/promotion/report/:pathParam',
		method: methods.GET,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
		headers: {
			Requester: '',
		},
	},
	getVoucherCampaignReports: {
		path: 'campaigns/voucher/report/:pathParam',
		method: methods.GET,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
		headers: {
			Requester: '',
		},
	},
	getPromotionCampaignDetails: {
		path: 'campaigns/promotion/:pathParam',
		method: methods.GET,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
		headers: {
			Requester: '',
		},
	},
	getVoucherCampaignDetails: {
		path: 'campaigns/voucher/:pathParam',
		method: methods.GET,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
		headers: {
			Requester: '',
		},
	},
	createPromotionCampaign: {
		path: 'campaigns/promotion',
		method: methods.POST,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
	},
	createVoucherCampaign: {
		path: 'campaigns/voucher',
		method: methods.POST,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
	},
	updatePromotionCampaign: {
		path: 'campaigns/promotion',
		method: methods.PUT,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
		headers: {
			Requester: '',
		},
	},
	updateVoucherCampaign: {
		path: 'campaigns/voucher',
		method: methods.PUT,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
		headers: {
			Requester: '',
		},
	},
	deletePromotionCampaign: {
		path: 'campaigns/promotion',
		method: methods.DELETE,
		ignoreCredentials: true,
		headers: {
			Requester: '',
		},
	},
	deleteVoucherCampaign: {
		path: 'campaigns/voucher',
		method: methods.DELETE,
		ignoreCredentials: true,
		headers: {
			Requester: '',
		},
	},
	validateCampaign: {
		path: 'campaigns/validation',
		method: methods.POST,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
		headers: {
			Requester: '',
		},
	},
	getCampaignsEnums: {
		path: 'campaigns/enums',
		method: methods.GET,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
	},
	updateEvoucher: {
		path: 'voucher/issuance',
		method: methods.POST,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
	},
	updateTargetedPromotion: {
		path: 'promotion/issuance',
		method: methods.POST,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
	},
	issueMultipleVoucher: {
		path: 'voucher/issuance/multiple',
		method: methods.POST,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
	},
	issueMultiplePromotion: {
		path: 'promotion/issuance/multiple',
		method: methods.POST,
		responseType: responseTypes.JSON,
		ignoreCredentials: true,
	},
};

const whiteListedPaths = Object.values(endpoints).map(
	(endpoint) => endpoint.path,
);

const resolveResponse = (response, failed, completed, noRedirect) => {
	let result = response;

	if (response.errorResponse) {
		if (
			response.errorResponse.code &&
			response.errorResponse.code === 401 &&
			!noRedirect
		) {
			window.localStorage.clear();
			window.location.href = getHomePageUrl();
			result = null;
		}

		if (failed) {
			result = failed(response);
		}
	} else if (response.error) {
		if (
			window.localStorage.getItem(LOCAL_STORAGE_KEYS.hasTriedRefresh) === 'true'
		) {
			store.dispatch(userLogout());
			// workaround for when 401 code is inaccessible but logging out is required
			setTimeout(() => {
				window.location.href = getHomePageUrl();
				result = null;
			}, 500);
		} else {
			// eslint-disable-next-line no-param-reassign
			response.errorResponse =
				typeof response.error === 'string'
					? { message: response.error }
					: response.error;
			if (failed) {
				result = failed(response);
			}
		}
	} else if (response?.message) {
		result = failed(response);
	} else if (completed) {
		result = completed(response);
	}
	return result;
};

const objectToQueryString = (queryString, urlOptions) => {
	let queryStringSerialized = queryString;
	if (typeof queryStringSerialized === 'object') {
		queryStringSerialized = qs.stringify(queryString, {
			skipNull: true,
			skipEmptyString: false,
			...urlOptions,
			filter: (key, value) =>
				!Array.isArray(value) || value.length > 0 ? value : undefined,
		});
	}

	return queryStringSerialized;
};

const getTokenDetails = () => {
	const state = store.getState();
	const accessToken = state.getIn(['auth', 'accessToken']) || '';
	const accessTokenExpiryTime = state.getIn(['auth', 'tokenExpiryTime']) || 0;
	return {
		accessToken,
		accessTokenExpiryTime,
	};
};

export const refreshToken = () =>
	new Promise((resolve, reject) => {
		// eslint-disable-next-line no-use-before-define
		apiCall(
			endpoints.getRefreshToken,
			{
				grant_type: 'refresh_token',
			},
			(error) => {
				store.dispatch(userLogout());
				window.location.href = getHomePageUrl();
				window.localStorage.clear();
				reject(error);
			},
			(response) => {
				const newToken = response.successResponse.data.SecurityAccessToken;
				const expiresIn = response.successResponse.data.SecurityExpiresIn;
				const currentTime = Date.now();
				const newExpiryTime = currentTime + expiresIn * 1000;

				store.dispatch(setAccessToken(newToken));
				store.dispatch(setAccessTokenExpiryTime(newExpiryTime));
				resolve(newToken);
			},
		);
	});

const getGenericHttpErrorMessage = (status, statusText) =>
	`HTTP ${status}: ${statusText || 'Request failed'}`;

const srtFetch = (
	domain,
	path,
	queryString,
	failed,
	completed,
	noRedirect,
	body,
	options,
	responseType,
) => {
	const url = `${domain}${path}${
		queryString ? `?${objectToQueryString(queryString, domain.urlOptions)}` : ''
	}`;
	const finalOptions = { ...options };
	const { customHeaders } = options;
	const { accessToken } = getTokenDetails();

	finalOptions.headers = {
		...finalOptions.headers,
		...(accessToken &&
		path !== `${BASE_PATH_SUFFIX}${endpoints.getRefreshToken.path}`
			? { Authorization: `Bearer ${accessToken}` }
			: {}),
		...customHeaders,
	};
	if (body) {
		finalOptions.body = JSON.stringify(body);
		finalOptions.headers['Content-Type'] = 'application/json';
	}
	const gaFunctions = gaEventApiCall(
		`/${path}`,
		queryString,
		failed,
		completed,
		body,
	);

	const fetchWithRetry = () => {
		let filteredPath;
		if (path?.includes(BASE_PATH_SUFFIX)) {
			filteredPath = path.split('/').slice(1).join('/');
		}
		const pathToCheck = filteredPath ?? path;

		// V1/Accounts and V1/Campaigns have a dynamic hash passed into the path so we have to be more generic when checking
		const validPathsToCheck =
			whiteListedPaths.includes(pathToCheck) ||
			path.includes('v1/accounts') ||
			path.includes('customer-accounts') ||
			path.includes('v1/campaigns') ||
			path.includes('v1/voucher') ||
			path.includes('v1/promotion') ||
			path.includes('external-payment-pci/operations');

		if (validPathsToCheck) {
			return fetch(
				`${url}${
					url.indexOf('?') === -1 ? '?' : '&'
				}_=${Date.now()}${Math.random()}`,
				finalOptions,
			).then((response) => {
				// this is to prevent an infinite loop
				if (
					response.status === 401 &&
					window.localStorage.getItem(LOCAL_STORAGE_KEYS.hasTriedRefresh) !==
						'true' &&
					window.localStorage.getItem(LOCAL_STORAGE_KEYS.loggedOut) !== 'true'
				) {
					window.localStorage.setItem(
						LOCAL_STORAGE_KEYS.hasTriedRefresh,
						'true',
					);

					return refreshToken()
						.then((newToken) => {
							if (newToken) {
								finalOptions.headers.Authorization = `Bearer ${newToken}`;
								return fetch(
									`${url}${
										url.indexOf('?') === -1 ? '?' : '&'
									}_=${Date.now()}${Math.random()}`,
									finalOptions,
								);
							}
							store.dispatch(userLogout());
							window.location.href = getHomePageUrl();
							window.localStorage.clear();
							return Promise.resolve();
						})
						.catch((error) => {
							store.dispatch(userLogout());
							window.location.href = getHomePageUrl();
							window.localStorage.clear();
							throw error;
						});
				}
				// For other non-ok responses (like 401, 403, etc.)
				if (!response.ok) {
					// For JSON responses
					if (responseType === responseTypes.JSON || responseType === null) {
						return response
							.json()
							.then((errorData) => {
								if (errorData?.errorResponse?.message) {
									return { errorResponse: errorData.errorResponse };
								}
								return {
									errorResponse: {
										code: response.status,
										message:
											errorData?.detail ||
											errorData?.message ||
											getGenericHttpErrorMessage(
												response.status,
												response.statusText,
											),
									},
								};
							})
							.catch(() => {
								throw new Error(
									getGenericHttpErrorMessage(
										response.status,
										response.statusText,
									),
								);
							});
					}

					throw new Error(
						getGenericHttpErrorMessage(response.status, response.statusText),
					);
				}
				return response;
			});
		}
		throw new Error(
			`The URL containing ${filteredPath} is not allowed to be called`,
		);
	};

	return fetchWithRetry()
		.then((response) => {
			if (response?.errorResponse) {
				return response;
			}
			if (responseType === responseTypes.TEXT) {
				return response.text();
			}
			if (responseType === responseTypes.JSON) {
				return response.json();
			}
			if (responseType === responseTypes.BINARY) {
				return response.arrayBuffer();
			}
			if (responseType === responseTypes.DOWNLOAD) {
				return response.arrayBuffer().then((buffer) => ({
					buffer,
					contentDisposition: response.headers.get('content-disposition'),
				}));
			}
			if (response.ok) {
				return {
					status: 'accepted',
					statusCode: response.status,
				};
			}
			return response.json();
		})
		.then((response) =>
			resolveResponse(
				response,
				gaFunctions.failed,
				gaFunctions.completed,
				noRedirect,
			),
		)
		.catch((error) =>
			resolveResponse(
				{ error },
				gaFunctions.failed,
				gaFunctions.completed,
				noRedirect,
			),
		);
};

const getApiDomainAndPath = (endpoint, backend, customPath) => {
	let domain = endpoint.domain || process.env.API_HOST;
	let path = `${endpoint.domain ? '' : BASE_PATH_SUFFIX}${endpoint.path}${
		customPath ?? ''
	}`;

	if (backend === Backend.BFF) {
		domain = process.env.BFF_HOST;
		path = `v1/${endpoint.path}`;
	}

	return { domain, path };
};

const apiCall = (
	endpoint,
	params,
	failed,
	completed,
	abortController,
	headers,
	customPath,
	backend = Backend.SA,
) => {
	const { domain, path } = getApiDomainAndPath(endpoint, backend, customPath);

	return srtFetch(
		domain,
		path,
		endpoint.method === methods.GET && params,
		failed,
		completed,
		endpoint.noRedirect,
		endpoint.method !== methods.GET ? params : undefined,
		endpoint.ignoreCredentials
			? {
					method: endpoint.method,
					signal: abortController && abortController.signal,
					headers: endpoint.headers,
				}
			: {
					...(!endpoint.domain && { credentials: 'include' }),
					method: endpoint.method,
					signal: abortController && abortController.signal,
					customHeaders: headers,
				},
		endpoint.responseType,
	);
};

export const asyncApiCall = (apiCallFn, ...params) =>
	new Promise((resolve, reject) => {
		apiCallFn(...params, reject, resolve);
	});

export const getCustomerAccountData = (
	parameters,
	customHeaders,
	accountId,
	failed,
	completed,
) =>
	apiCall(
		endpoints.getCustomerAccountData,
		parameters,
		failed,
		completed,
		null,
		customHeaders,
		accountId,
	);

export const postCustomerAccountData = (
	parameters,
	customHeaders,
	accountId,
	failed,
	completed,
) =>
	apiCall(
		{
			...endpoints.getCustomerAccountData,
			method: methods.POST,
			responseType: null, // responseType set to null to Process 200-299 status codes as success
		},
		parameters,
		failed,
		completed,
		null,
		customHeaders,
		accountId,
	);

export const postCustomerAccountAction = (
	parameters,
	customHeaders,
	customPath,
	failed,
	completed,
) =>
	apiCall(
		endpoints.customerAccountActions,
		parameters,
		failed,
		completed,
		null,
		customHeaders,
		customPath,
	);

export const patchCustomerAccountData = (
	parameters,
	failed,
	completed,
	customHeaders,
	accountId,
) =>
	apiCall(
		{
			...endpoints.getCustomerAccountData,
			method: methods.PATCH,
			responseType: null, // responseType set to null to Process 200-299 status codes as success
		},
		parameters,
		failed,
		completed,
		null,
		customHeaders,
		accountId,
	);

export const deleteCustomerAccountData = (
	parameters,
	failed,
	completed,
	customHeaders,
	accountId,
) =>
	apiCall(
		{
			...endpoints.getCustomerAccountData,
			method: methods.DELETE,
			responseType: null, // responseType set to null to Process 200-299 status codes as success
		},
		parameters,
		failed,
		completed,
		null,
		customHeaders,
		accountId,
	);

export const confirmBookingChange = (parameters, failed, completed) =>
	apiCall(endpoints.confirmBookingChange, parameters, failed, completed);
export const confirmRefundOffer = (offerId, parameters, failed, completed) =>
	apiCall(
		{
			...endpoints.getRefundOffers,
			path: `${endpoints.getRefundOffers.path}/${offerId}`,
		},
		parameters,
		failed,
		completed,
	);
export const validateShopping = (parameters, failed, completed) =>
	apiCall(endpoints.validateShopping, parameters, failed, completed);
export const getRefundOffers = (parameters, failed, completed) =>
	apiCall(endpoints.getRefundOffers, parameters, failed, completed);
export const getExternalPaymentPciUrl = (parameters, failed, completed) =>
	apiCall(endpoints.getExternalPaymentPciUrl, parameters, failed, completed);

export const getExternalPaymentPciOperationStatus = (
	operationId,
	parameters,
	failed,
	completed,
) =>
	apiCall(
		{
			...endpoints.getExternalPaymentPciOperationStatus,
			path: endpoints.getExternalPaymentPciOperationStatus.path.replace(
				':operationId',
				operationId,
			),
		},
		parameters,
		failed,
		completed,
	);

export const getPaymentContextType = (parameters, failed, completed) =>
	apiCall(endpoints.getPaymentContextType, parameters, failed, completed);
export const getIntermediateTravelPoints = (parameters, failed, completed) =>
	apiCall(endpoints.getIntermediateTravelPoints, parameters, failed, completed);
export const getJourneysList = (parameters, failed, completed) =>
	apiCall(endpoints.getJourneysList, parameters, failed, completed);
export const getAccountUsers = (parameters, failed, completed) =>
	apiCall(endpoints.getAccountUsers, parameters, failed, completed);
export const getSmartCardPrintingLocations = (parameters, failed, completed) =>
	apiCall(
		endpoints.getSmartCardPrintingLocations,
		undefined,
		failed,
		completed,
		null,
		parameters?.headers,
	);
export const submitPrintSmartcard = (parameters, headers, failed, completed) =>
	apiCall(
		endpoints.printSmartcard,
		parameters,
		failed,
		completed,
		null,
		headers,
	);
export const getUserProfile = (parameters, failed, completed) =>
	apiCall(endpoints.getUserProfile, parameters, failed, completed);
export const addUserProfile = (parameters, failed, completed) =>
	apiCall(endpoints.addUserProfile, parameters, failed, completed);
export const updateUserProfile = (parameters, failed, completed) =>
	apiCall(endpoints.updateUserProfile, parameters, failed, completed);
export const getUserSettings = (widgetList) =>
	apiCall(endpoints.getUserSettings, { widgetList });
export const userSearch = (parameters, failed, completed) =>
	apiCall(endpoints.userSearch, parameters, failed, completed);
export const getRoles = (failed, completed) =>
	apiCall(endpoints.getRoles, undefined, failed, completed);
export const deleteUser = (parameters, failed, completed) =>
	apiCall(endpoints.deleteUser, parameters, failed, completed);
export const accountSearch = (parameters, failed, completed) =>
	apiCall(endpoints.accountSearch, parameters, failed, completed);
export const getAccountGroupData = (parameters, failed, completed) =>
	apiCall(endpoints.getAccountGroupData, parameters, failed, completed);
export const getAccountGroupLabels = (parameters, failed, completed) =>
	apiCall(endpoints.getAccountGroupLabels, parameters, failed, completed);
export const getAccountLabels = (parameters, failed, completed) =>
	apiCall(endpoints.getAccountLabels, parameters, failed, completed);
export const submitAccountGroupData = (parameters, failed, completed) =>
	apiCall(endpoints.submitAccountGroupData, parameters, failed, completed);
export const deleteAccountGroup = (parameters, failed, completed) =>
	apiCall(endpoints.deleteAccountGroup, parameters, failed, completed);
export const getAccountData = (parameters, failed, completed) =>
	apiCall(endpoints.getAccountData, parameters, failed, completed);
export const submitAccountData = (parameters, failed, completed) =>
	apiCall(endpoints.submitAccountData, parameters, failed, completed);
export const deleteAccount = (parameters, failed, completed) =>
	apiCall(endpoints.deleteAccount, parameters, failed, completed);
export const logOut = (failed, completed) =>
	apiCall(endpoints.logOut, undefined, failed, completed);
export const changeUserPassword = (parameters, failed, completed) =>
	apiCall(endpoints.changeUserPassword, parameters, failed, completed);
// SHOPPING CALLS
export const stationSearch = (parameters, failed, completed, abortController) =>
	apiCall(
		endpoints.stationSearch,
		parameters,
		failed,
		completed,
		abortController,
	);
export const refreshUserState = (failed, completed) =>
	apiCall(endpoints.refreshUserState, undefined, failed, completed);
export const shoppingSearch = (parameters, failed, completed) =>
	apiCall(endpoints.shoppingSearch, parameters, failed, completed);
export const bookingValidate = (parameters, failed, completed) =>
	apiCall(endpoints.bookingValidate, parameters, failed, completed);
export const tdoSmsCallingCodes = (parameters, failed, completed) =>
	apiCall(endpoints.tdoSmsCallingCodes, parameters, failed, completed);
export const bookingCreate = (parameters, failed, completed) =>
	apiCall(endpoints.bookingCreate, parameters, failed, completed);
export const bookingSetTdo = (parameters, failed, completed) =>
	apiCall(endpoints.bookingSetTdo, parameters, failed, completed);
export const updateCustomInfo = (parameters, failed, completed) =>
	apiCall(endpoints.updateCustomInfo, parameters, failed, completed);
// MANAGE CALLS
export const bookingSearch = (parameters, failed, completed) =>
	apiCall(endpoints.bookingSearch, parameters, failed, completed);
export const loadBookingDetails = (parameters, failed, completed) =>
	apiCall(endpoints.loadBookingDetails, parameters, failed, completed);
export const getBookingAmendmentsInfo = (parameters, failed, completed) =>
	apiCall(endpoints.getBookingAmendmentsInfo, parameters, failed, completed);
export const addBookingNote = (parameters, failed, completed) =>
	apiCall(endpoints.addBookingNote, parameters, failed, completed);
export const addSupplierNote = (parameters, failed, completed) =>
	apiCall(endpoints.addSupplierNote, parameters, failed, completed);
export const addCustomInfo = (parameters, failed, completed) =>
	apiCall(endpoints.addCustomInfo, parameters, failed, completed);
export const getPaymentDue = (parameters, failed, completed) =>
	apiCall(endpoints.getPaymentDue, parameters, failed, completed);
export const submitPayment = (parameters, failed, completed) =>
	apiCall(endpoints.submitPayment, parameters, failed, completed);
export const getOrderDetails = (parameters, failed, completed) =>
	apiCall(endpoints.getOrderDetails, parameters, failed, completed);
export const getCancelOrderDetails = (parameters, failed, completed) =>
	apiCall(endpoints.getCancelOrderDetails, parameters, failed, completed);
export const applyFeeDiscount = (parameters, failed, completed) =>
	apiCall(endpoints.applyFeeDiscount, parameters, failed, completed);
export const submitCancelBooking = (parameters, failed, completed) =>
	apiCall(endpoints.submitCancelBooking, parameters, failed, completed);
export const submitOrderCancel = (parameters, failed, completed) =>
	apiCall(endpoints.submitOrderCancel, parameters, failed, completed);
export const getCancellationPenalty = (parameters, failed, completed) =>
	apiCall(endpoints.getCancellationPenalty, parameters, failed, completed);
export const getRefundDetails = (parameters, failed, completed) =>
	apiCall(endpoints.getRefundDetails, parameters, failed, completed);
export const submitRefund = (parameters, failed, completed) =>
	apiCall(endpoints.submitRefund, parameters, failed, completed);
export const bookingAddOrder = (parameters, failed, completed) =>
	apiCall(endpoints.bookingAddOrder, parameters, failed, completed);
export const getConfirmDetails = (parameters, failed, completed) =>
	apiCall(endpoints.getConfirmDetails, parameters, failed, completed);
export const confirmOrder = (parameters, failed, completed) =>
	apiCall(endpoints.confirmOrder, parameters, failed, completed);
export const addFeeDiscount = (parameters, failed, completed) =>
	apiCall(endpoints.addFeeDiscount, parameters, failed, completed);
export const amendCTR = (parameters, failed, completed) =>
	apiCall(endpoints.amendCTR, parameters, failed, completed);
export const loadEmailConfirmation = (parameters, failed, completed) =>
	apiCall(endpoints.loadEmailConfirmation, parameters, failed, completed);
export const sendEmailConfirmation = (parameters, failed, completed) =>
	apiCall(endpoints.sendEmailConfirmation, parameters, failed, completed);
export const getTrackingInfo = (parameters, failed, completed) =>
	apiCall(endpoints.getTrackingInfo, parameters, failed, completed);
export const setTrackingInfo = (parameters, failed, completed) =>
	apiCall(endpoints.setTrackingInfo, parameters, failed, completed);
export const getCoupons = (parameters, failed, completed) =>
	apiCall(endpoints.getCoupons, parameters, failed, completed);
export const setCoupons = (parameters, failed, completed) =>
	apiCall(endpoints.setCoupons, parameters, failed, completed);
export const getStationTOD = (parameters, failed, completed) =>
	apiCall(endpoints.getStationTOD, parameters, failed, completed);
export const getSmartCardTdoStations = (parameters, failed, completed) =>
	apiCall(endpoints.getSmartCardTdoStations, parameters, failed, completed);
export const getStationData = (parameters, failed, completed) =>
	apiCall(endpoints.getStationData, parameters, failed, completed);
export const getAccessToken = (parameters, failed, completed) =>
	apiCall(endpoints.getAccessToken, parameters, failed, completed);
export const getRefreshToken = (parameters, failed, completed) =>
	apiCall(endpoints.getRefreshToken, parameters, failed, completed);
export const resetUserPassword = (parameters, failed, completed) =>
	apiCall(endpoints.resetUserPassword, parameters, failed, completed);
export const resetUserPasswordTokenCheck = (parameters, failed, completed) =>
	apiCall(endpoints.resetUserPasswordTokenCheck, parameters, failed, completed);
export const forgotPassword = (parameters, failed, completed) =>
	apiCall(endpoints.forgotPassword, parameters, failed, completed);
export const isAuth = (failed, completed) =>
	apiCall(endpoints.isAuth, undefined, failed, completed);
export const getAuthRefunds = (parameters, failed, completed) =>
	apiCall(endpoints.getAuthRefunds, parameters, failed, completed);
export const setAuthRefunds = (parameters, failed, completed) =>
	apiCall(endpoints.setAuthRefunds, parameters, failed, completed);
export const acceptTandC = (parameters, failed, completed) =>
	apiCall(endpoints.acceptTandC, parameters, failed, completed);
export const resendClaimTicket = (parameters, failed, completed) =>
	apiCall(endpoints.resendClaimTicket, parameters, failed, completed);
export const sendTicket = (parameters, failed, completed) =>
	apiCall(endpoints.sendTicket, parameters, failed, completed);
export const getExchangeOrderDetails = (parameters, failed, completed) =>
	apiCall(endpoints.getExchangeOrderDetails, parameters, failed, completed);
export const exchangeSearch = (parameters, failed, completed) =>
	apiCall(endpoints.exchangeSearch, parameters, failed, completed);
export const exchangeSummary = (parameters, failed, completed) =>
	apiCall(endpoints.exchangeSummary, parameters, failed, completed);
export const processExchange = (parameters, failed, completed) =>
	apiCall(endpoints.processExchange, parameters, failed, completed);
export const getAnalytics = () => apiCall(endpoints.getAnalytics);
export const getPaymentData = (parameters, failed, completed) =>
	apiCall(endpoints.getPaymentData, parameters, failed, completed);
export const deletePersonalInformation = (parameters, failed, completed) =>
	apiCall(endpoints.deletePersonalInformation, parameters, failed, completed);
export const submitPassengerInformation = (parameters, failed, completed) =>
	apiCall(endpoints.submitPassengerInformation, parameters, failed, completed);
export const updatePassengerCustomInformation = (
	parameters,
	failed,
	completed,
) =>
	apiCall(
		endpoints.updatePassengerCustomInformation,
		parameters,
		failed,
		completed,
	);
export const updateCardCollection = (parameters, failed, completed) =>
	apiCall(endpoints.updateCardCollection, parameters, failed, completed);
export const suspendSecureTicket = (parameters, failed, completed) =>
	apiCall(endpoints.suspendSecureTicket, parameters, failed, completed);
export const activateSecureTicket = (parameters, failed, completed) =>
	apiCall(endpoints.activateSecureTicket, parameters, failed, completed);
export const resetSecureTicketDeviceId = (parameters, failed, completed) =>
	apiCall(endpoints.resetSecureTicketDeviceId, parameters, failed, completed);
export const resetMobileTicketDeviceId = (parameters, failed, completed) =>
	apiCall(endpoints.resetMobileTicketDeviceId, parameters, failed, completed);
export const getFeatureToggles = (failed, completed) =>
	apiCall(endpoints.getFeatureToggles, undefined, failed, completed);
export const toggleFeature = (parameters, failed, completed) =>
	apiCall(endpoints.toggleFeature, parameters, failed, completed);
export const getSeatMap = (parameters, failed, completed) =>
	apiCall(endpoints.getSeatMap, parameters, failed, completed);
export const getExportedUsers = (parameters, failed, completed) =>
	apiCall(endpoints.getExportedUsers, parameters, failed, completed);

// OUIGO REFUNDS
export const getEligibleOuigoVouchers = (parameters, failed, completed) =>
	apiCall(endpoints.getEligibleOuigoVouchers, parameters, failed, completed);
export const getFinalizedOuigoVouchers = (parameters, failed, completed) =>
	apiCall(endpoints.getFinalizedOuigoVouchers, parameters, failed, completed);
export const finalizeOuigoVouchers = (parameters, failed, completed) =>
	apiCall(endpoints.finalizeOuigoVouchers, parameters, failed, completed);
export const exportFinalizedOuigoVouchers = (parameters, failed, completed) =>
	apiCall(
		endpoints.exportFinalizedOuigoVouchers,
		parameters,
		failed,
		completed,
	);

// CAMPAIGNS
export const getCampaigns = (parameters, requester, failed, completed) =>
	apiCall(
		{ ...endpoints.getCampaigns, headers: { requester } },
		parameters,
		failed,
		completed,
		null,
		{},
		'',
		Backend.BFF,
	);

export const getPromotionCampaignReports = (
	parameters,
	requester,
	pathParam,
	failed,
	completed,
) =>
	apiCall(
		{
			...endpoints.getPromotionCampaignReports,
			path: endpoints.getPromotionCampaignReports.path.replace(
				':pathParam',
				pathParam,
			),
			headers: {
				Requester: requester,
			},
		},
		parameters,
		failed,
		completed,
		null,
		null,
		'',
		Backend.BFF,
	);

export const getVoucherCampaignReports = (
	parameters,
	requester,
	pathParam,
	failed,
	completed,
) =>
	apiCall(
		{
			...endpoints.getVoucherCampaignReports,
			path: endpoints.getVoucherCampaignReports.path.replace(
				':pathParam',
				pathParam,
			),
			headers: {
				Requester: requester,
			},
		},
		parameters,
		failed,
		completed,
		null,
		null,
		'',
		Backend.BFF,
	);

export const getPromotionCampaignDetails = (
	parameters,
	requester,
	pathParam,
	failed,
	completed,
) =>
	apiCall(
		{
			...endpoints.getPromotionCampaignDetails,
			path: endpoints.getPromotionCampaignDetails.path.replace(
				':pathParam',
				pathParam,
			),
			headers: {
				Requester: requester,
			},
		},
		parameters,
		failed,
		completed,
		null,
		null,
		'',
		Backend.BFF,
	);

export const getVoucherCampaignDetails = (
	parameters,
	requester,
	pathParam,
	failed,
	completed,
) =>
	apiCall(
		{
			...endpoints.getVoucherCampaignDetails,
			path: endpoints.getVoucherCampaignDetails.path.replace(
				':pathParam',
				pathParam,
			),
			headers: {
				Requester: requester,
			},
		},
		parameters,
		failed,
		completed,
		null,
		null,
		'',
		Backend.BFF,
	);

export const createPromotionCampaign = (
	parameters,
	requester,
	failed,
	completed,
) =>
	apiCall(
		{
			...endpoints.createPromotionCampaign,
			headers: {
				Requester: requester,
			},
		},
		parameters,
		failed,
		completed,
		null,
		null,
		'',
		Backend.BFF,
	);

export const createVoucherCampaign = (
	parameters,
	requester,
	failed,
	completed,
) =>
	apiCall(
		{
			...endpoints.createVoucherCampaign,
			headers: {
				Requester: requester,
			},
		},
		parameters,
		failed,
		completed,
		null,
		null,
		'',
		Backend.BFF,
	);

export const updatePromotionCampaign = (
	parameters,
	requester,
	failed,
	completed,
) =>
	apiCall(
		{ ...endpoints.updatePromotionCampaign, headers: { Requester: requester } },
		parameters,
		failed,
		completed,
		null,
		null,
		'',
		Backend.BFF,
	);

export const updateVoucherCampaign = (
	parameters,
	requester,
	failed,
	completed,
) =>
	apiCall(
		{ ...endpoints.updateVoucherCampaign, headers: { Requester: requester } },
		parameters,
		failed,
		completed,
		null,
		null,
		'',
		Backend.BFF,
	);

export const deletePromotionCampaign = (
	parameters,
	requester,
	failed,
	completed,
) =>
	apiCall(
		{
			...endpoints.deletePromotionCampaign,
			path: `${endpoints.deletePromotionCampaign.path}/${parameters.campaignCode}`,
			headers: { Requester: requester },
		},
		parameters,
		failed,
		completed,
		null,
		null,
		'',
		Backend.BFF,
	);

export const deleteVoucherCampaign = (
	parameters,
	requester,
	failed,
	completed,
) =>
	apiCall(
		{
			...endpoints.deleteVoucherCampaign,
			path: `${endpoints.deleteVoucherCampaign.path}/${parameters.campaignCode}`,
			headers: { Requester: requester },
		},
		parameters,
		failed,
		completed,
		null,
		null,
		'',
		Backend.BFF,
	);

export const validateCampaign = (parameters, requester, failed, completed) =>
	apiCall(
		{
			...endpoints.validateCampaign,
			headers: { Requester: requester },
		},
		parameters,
		failed,
		completed,
		null,
		null,
		null,
		Backend.BFF,
	);

export const getCampaignsEnums = (requester, failed, completed) =>
	apiCall(
		{ ...endpoints.getCampaignsEnums, headers: { Requester: requester } },
		undefined,
		failed,
		completed,
		null,
		null,
		'',
		Backend.BFF,
	);

export const updateEVoucher = (parameters, requester, failed, completed) =>
	apiCall(
		{ ...endpoints.updateEvoucher, headers: { requester } },
		parameters,
		failed,
		completed,
		null,
		{},
		'',
		Backend.BFF,
	);

export const updateTargetedPromotion = (
	parameters,
	requester,
	failed,
	completed,
) =>
	apiCall(
		{ ...endpoints.updateTargetedPromotion, headers: { requester } },
		parameters,
		failed,
		completed,
		null,
		{},
		'',
		Backend.BFF,
	);

export const issueMultipleVoucher = (
	parameters,
	requester,
	failed,
	completed,
) =>
	apiCall(
		{ ...endpoints.issueMultipleVoucher, headers: { requester } },
		parameters,
		failed,
		completed,
		null,
		{},
		'',
		Backend.BFF,
	);

export const issueMultiplePromotion = (
	parameters,
	requester,
	failed,
	completed,
) =>
	apiCall(
		{ ...endpoints.issueMultiplePromotion, headers: { requester } },
		parameters,
		failed,
		completed,
		null,
		{},
		'',
		Backend.BFF,
	);
